import { type BaseListResponse, SortBy } from '$lib/models/types';
import type { ClientContractSummary } from '$lib/types';
import { fetchWithAuth } from '$lib/utils/http/httpUtil';
import { extractErrorMessage, handleApiError } from '$lib/utils/error/errorUtils';
import { showToast } from '$lib/helpers/toastrHelper';
import type { State } from '@vincjo/datatables/local';
import { writable } from 'svelte/store';

function createClientContractConfigStore() {
  const baseUrl: string = '/api/contract';

  return {
    fetch: async (state: State): Promise<BaseListResponse> => {
      const { pageNumber, rowsPerPage, sort, filters } = state;

      let url = `${baseUrl}/summary`;

      const sortBy: string =
        sort?.direction?.toString() != undefined ? (sort?.direction?.toString().toLowerCase() == 'asc' ? SortBy.Ascending : SortBy.Descending) : SortBy.Descending;

      url += `?pageNumber=${pageNumber}&pageSize=${rowsPerPage}&sortBy=${sortBy}&orderBy=${sort?.orderBy}`;
      if (filters) {
        url += filters
          .map(({ filterBy, value }) => {

            return `&${filterBy}=${value}`;
          })
          .join('');
      }

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, `Failed to fetch Client Contracts (HTTP ${response.status})`);
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return { total: 0, data: [] };
      }

      try {
        return await response.json();
      } catch (error) {
        const errorMessage = handleApiError(error as Error, 'Failed to parse Client Contract data');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return { total: 0, data: [] };
      }
    },
    getContractDetails: async (id: number): Promise<ClientContractSummary | null> => {
      try {
        const response = await fetchWithAuth(`${baseUrl}/detail/${id}`);
        if (!response.ok) {
          const errorMessage = await extractErrorMessage(response, `Failed to fetch SSO config details (HTTP ${response.status})`);
          showToast({
            title: 'Error',
            message: errorMessage,
            type: 'error'
          });
          throw new Error(errorMessage);
        }
        return await response.json();
      } catch (error) {
        const errorMessage = handleApiError(error as Error, 'Error fetching SSO config details');
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error'
        });
        return null;
      }
    },
    updateContract: async (updatedContract: ClientContractSummary) => {

        try {
            const response = await fetchWithAuth(`${baseUrl}/updateContract`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updatedContract),
            });
            if (!response.ok) {
                const errorBody = await response.json();
                throw new Error(`Failed to update SSO config: ${errorBody.detail || errorBody}`);
            }

            const returnedContract: ClientContractSummary = await response.json();
            return returnedContract;
        } catch (error) {
            const errorMessage = handleApiError(error as Error, 'Error updating Client Contract');
            showToast({
                title: 'Error',
                message: errorMessage,
                type: 'error'
            });
            throw error;
        }
      },
  };
}

export const clientContractConfigStore = createClientContractConfigStore();
